import { useMutation } from '@apollo/client';
import styled from "@emotion/styled";
import { Checkbox } from "@kompare/kompare-ui-library";
import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import openLink from "src/assets/images/open-link.png";
import { closeAddressModal, i18n, mediaQuery, navigate, openTermsAndConditionsAdditionalModal, RootState, setGlobalLoading, setIsContractorSameAsInsuredPersonStore, setOfferId, setProgressBarPercentage, setSoId, setSoIdWeb, showMessage, useAppDispatch, useAppSelector, useForm, useRenewalOf } from 'src/common';
import { acceptTermsAdditionalMutation, AcceptTermsMutation, AcceptTermsMutationVariables, ContractorInput, ContractorPartInput, DeliveryInput, GetOfferQuery, InsuredPersonInput, setContractorAdditionalMutation, SetContractorMutation, SetContractorMutationVariables, useGetOfferQuery } from 'src/common/api/apollo/additional';
import { AddressModal, allInsuredPersonFormItems, BackLink, Button, Card, CheckboxRequirement, ContractorForm, DeliveryForm, Flex, InsuredPersonForm, Loader, SuperFooterBar, Text } from 'src/components';
import { colors } from 'src/theme';
import { AppRoutesAdditional } from '../../_router/app.routes';


const TextStyle = styled(Text)({
  marginBottom: 32
})

const TextStyle2 = styled(Text)({
  marginBottom: 16
})

const CardStyle = styled(Card)({
  padding: '24px 12px',

  [mediaQuery('mobile')]: {
    padding: '24px 20px'
  }
})

const TermsAndConditionsCard = styled.div({
  padding: '16px 12px',
  backgroundColor: 'transparent',
  border: '1px dashed #E0E0E0',
  borderColor: colors.text4,
  borderRadius: 8,
  display: 'flex',
  justifyContent: 'flex-start',

  [mediaQuery('mobile')]: {
    padding: '16px 20px'
  }
})

const TermsAndConditionsButton = styled.div({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: 0,
  backgroundColor: 'transparent',
  border: 'none',
  cursor: 'pointer',
  color: colors.primary,

  '&:hover': {
    opacity: 0.8
  }
});

const OpenLinkStyle = styled.img({
  width: 16,
  height: 16,
});

const EDITABLE_FIELD = { value: '', isEditable: true };

/** Converts flat additional API shape to { value, isEditable } shape expected by shared forms */
function toEditableShape(flat: Record<string, unknown> | null | undefined): Record<string, { value: unknown; isEditable: boolean }> {
  if (!flat || typeof flat !== 'object') return {};
  return Object.fromEntries(
    Object.entries(flat)
      .filter(([k]) => k !== '__typename')
      .map(([k, v]) => [k, { value: v ?? '', isEditable: true }])
  );
}

/** Default insuredPerson keys read by InsuredPersonForm for .isEditable */
const insuredPersonDefaultKeys = ['firstName', 'lastName', 'birthDate', 'oib', 'insuranceNumber', 'city', 'cityKompareId', 'street', 'streetKompareId', 'houseNumber', 'partnerContactEmail'];

function mapFormDataForForms(productTypeSpecific: NonNullable<GetOfferQuery['getOffer']>['productTypeSpecific'] | null | undefined) {
  if (!productTypeSpecific) return null;
  const { insuredPerson: rawIp, contractor: rawContractor, delivery: rawDelivery } = productTypeSpecific;

  const insuredPersonRaw = (rawIp ?? {}) as Record<string, unknown>;
  const insuredPersonMapped = {
    ...Object.fromEntries(insuredPersonDefaultKeys.map((k) => [k, EDITABLE_FIELD])),
    ...toEditableShape(insuredPersonRaw),
  };
  const insuredPerson = {
    ...insuredPersonMapped,
    birthDate: { ...insuredPersonMapped.birthDate, isEditable: false },
    partnerContactEmail: { ...insuredPersonMapped.partnerContactEmail, isEditable: false },
  };
  const deliveryRaw = (rawDelivery ?? {}) as Record<string, unknown>;
  const deliveryDefaults: Record<string, { value: unknown; isEditable: boolean }> = {
    name: EDITABLE_FIELD,
    city: EDITABLE_FIELD,
    cityKompareId: EDITABLE_FIELD,
    street: EDITABLE_FIELD,
    streetKompareId: EDITABLE_FIELD,
    houseNumber: EDITABLE_FIELD,
  };
  const delivery = {
    ...deliveryDefaults,
    ...toEditableShape(deliveryRaw),
    asEntity: { value: (rawDelivery as { asEntity?: string })?.asEntity ?? 'insured', isEditable: true },
    name: {
      value: rawDelivery ? `${(rawDelivery as { firstName?: string }).firstName ?? ''} ${(rawDelivery as { lastName?: string }).lastName ?? ''}`.trim() : '',
      isEditable: true,
    },
  };
  const contractor = rawContractor
    ? {
      personType: 'person' as const,
      isSamePerson: (rawContractor as { isSamePerson?: boolean }).isSamePerson,
      person: toEditableShape(rawContractor as Record<string, unknown>),
      company: undefined,
    }
    : {
      personType: 'person' as const,
      isSamePerson: undefined,
      person: {} as Record<string, { value: unknown; isEditable: boolean }>,
      company: undefined,
    };

  return {
    ...productTypeSpecific,
    insuredPerson,
    delivery,
    contractor,
  };
}

const OfferAdditinalPage = () => {
  const insuredPersonForm = useForm();
  const contractorForm = useForm();
  const deliveryForm = useForm();
  const flow = useAppSelector((store: RootState) => store.state.flow);

  const soId = useAppSelector((store: RootState) => store.model.offer.soIdWeb);
  const { isRenewal } = useRenewalOf(soId);

  // Ref za terms agreement checkbox
  const termsCheckboxRef = useRef<HTMLDivElement>(null);

  const dispatch = useAppDispatch();

  const params = useParams<{ offerId: string }>();
  const offerId = params.offerId as string;

  const [isContractorSameAsInsuredPerson, setIsContractorSameAsInsuredPerson] = useState(true);
  const [isTermsAgreementChecked, setIsTermsAgreementChecked] = useState(false);
  const [isCheckboxRequirementVisible, setIsCheckboxRequirementVisible] = useState(false);
  const [addressData, setAddressData] = useState<{ insuredPersonAddress?: any, contractorAddress?: any, validation?: boolean }>({ insuredPersonAddress: {}, contractorAddress: {}, validation: false });

  const [isAllianzChecked, setIsAllianzChecked] = useState(false);

  const storedFormValues = useAppSelector((store) => store.model.offer.formValues);
  const storedIsContractorSameAsInsuredPerson = useAppSelector((store) => store.model.offer.isContractorSameAsInsuredPerson);

  const setIsSame = (value: boolean) => {
    setIsContractorSameAsInsuredPerson(value);
    dispatch(setIsContractorSameAsInsuredPersonStore(value));
  }

  const { data: getOfferAdditionalData, loading: loadingOfferAdditional } = useGetOfferQuery({
    variables: { id: offerId || '' },
    skip: !offerId,
    fetchPolicy: 'network-only',
    onError: () => {
      navigate(AppRoutesAdditional.NO_OFFER.fullPath);
    },
    onCompleted: (data) => {
      if (!data.getOffer?.productTypeSpecific?.healthQuestionnaire?.isConfirmed ) {
        navigate(`${AppRoutesAdditional.HEALTH_QUESTIONNAIRE_NEEDS_CONFIRMATION.fullPath}/${params.offerId}`);
      }
      if (data.getOffer?.productTypeSpecific?.healthQuestionnaire?.rejected) {
        navigate(`${AppRoutesAdditional.HEALTH_QUESTIONNAIRE_REJECTED.fullPath}/${params.offerId}`);
      }
    }
  });

  const [acceptTerms] = useMutation<AcceptTermsMutation, AcceptTermsMutationVariables>(acceptTermsAdditionalMutation)

  const [setContractor] = useMutation<SetContractorMutation, SetContractorMutationVariables>(setContractorAdditionalMutation)

  const storeIsSame = (value: boolean) => {
    dispatch(setIsContractorSameAsInsuredPersonStore(value));
    setIsContractorSameAsInsuredPerson(value);
  }


  useEffect(() => {
    if (getOfferAdditionalData) {
      const { getOffer: data } = getOfferAdditionalData || {};
      setIsSame(storedIsContractorSameAsInsuredPerson !== undefined
        ? storedIsContractorSameAsInsuredPerson
        : (data?.productTypeSpecific?.contractor?.isSamePerson ?? true));
      if (data) {
        setAddressData({
          insuredPersonAddress: data.productTypeSpecific?.insuredPerson ? {
            name: `${data.productTypeSpecific?.insuredPerson.firstName || ""} ${data.productTypeSpecific?.insuredPerson.lastName || ""}`.trim() || storedFormValues?.insuredPerson?.fullName,
            street: data.productTypeSpecific?.insuredPerson.street || storedFormValues?.insuredPerson?.street,
            houseNumber: data.productTypeSpecific?.insuredPerson.houseNumber || storedFormValues?.insuredPerson?.houseNumber,
            city: data.productTypeSpecific?.insuredPerson.city || storedFormValues?.insuredPerson?.city,
            zipCode: data.productTypeSpecific?.insuredPerson.zipCode || storedFormValues?.insuredPerson?.zipCode,
            streetKompareId: data.productTypeSpecific?.insuredPerson.streetKompareId || storedFormValues?.insuredPerson?.streetKompareId,
            cityKompareId: data.productTypeSpecific?.insuredPerson.cityKompareId || storedFormValues?.insuredPerson?.cityKompareId
          } : undefined,
          contractorAddress: data.productTypeSpecific?.contractor ? {
            name: (data.productTypeSpecific?.contractor ? `${data.productTypeSpecific?.contractor.firstName ?? ""} ${data.productTypeSpecific?.contractor.lastName ?? ""}`.trim() : "") || (storedFormValues?.contractor?.fullName || storedFormValues?.contractor?.companyName),
            street: (data.productTypeSpecific?.contractor ? data.productTypeSpecific?.contractor.street : "") || storedFormValues?.contractor?.street,
            houseNumber: (data.productTypeSpecific?.contractor ? data.productTypeSpecific?.contractor.houseNumber : "") || storedFormValues?.contractor?.houseNumber,
            city: (data.productTypeSpecific?.contractor ? data.productTypeSpecific?.contractor.city : "") || storedFormValues?.contractor?.city,
            zipCode: (data.productTypeSpecific?.contractor ? data.productTypeSpecific?.contractor.zipCode : "") || storedFormValues?.contractor?.zipCode,
            streetKompareId: (data.productTypeSpecific?.contractor ? data.productTypeSpecific?.contractor.streetKompareId : "") || storedFormValues?.contractor?.streetKompareId,
            cityKompareId: (data.productTypeSpecific?.contractor ? data.productTypeSpecific?.contractor.cityKompareId : "") || storedFormValues?.contractor?.cityKompareId,
          } : undefined,
          validation: false
        });
      }
      dispatch(setSoIdWeb(data?.soId));
      dispatch(setSoId(data?.soId));
    }

  }, [getOfferAdditionalData?.getOffer]);

  useEffect(() => {
    dispatch(setProgressBarPercentage(80));
  }, []);

  useEffect(() => {
    dispatch(setOfferId(offerId));
  }, [offerId]);

  const navigateToNoOffer = () => {
    navigate(AppRoutesAdditional.NO_OFFER.fullPath);
  };

  const rawFormData = getOfferAdditionalData?.getOffer?.productTypeSpecific;
  const formData = rawFormData ? mapFormDataForForms(rawFormData) : null;

  const backPath = (() => {
    if (flow.flowType === 'agent') return `${AppRoutesAdditional.AGENT_OFFERS_PREVIEW.fullPath}/${soId}`;
    if (isRenewal && soId) return `${AppRoutesAdditional.OFFERS_PREVIEW.fullPath}/${soId}`;
    return AppRoutesAdditional.OFFERS_PREVIEW.fullPath;
  })();

  const onSubmitClick = async () => {
    dispatch(setGlobalLoading(true));
    const isInsuredPersonFormValid = insuredPersonForm.validateForm();
    const isContractorFormValid = isContractorSameAsInsuredPerson ? true : contractorForm.validateForm();
    const isDeliveryFormValid = deliveryForm.validateForm();



    if (!isInsuredPersonFormValid) {
      insuredPersonForm.scrollIntoView();
      dispatch(setGlobalLoading(false));
      return;
    }

    if (!isContractorFormValid && !isContractorSameAsInsuredPerson) {
      contractorForm.scrollIntoView();
      dispatch(setGlobalLoading(false));
      return;
    }

    // Mapiranje podataka na izlazu iz forme - delivery -- ubačeno je ovdje jer je validacija potrebna samo u slučaju asEntity = 'third'
    const delivery = deliveryForm.getValues();
    delivery.streetsFound = undefined;

    if (!isDeliveryFormValid && delivery.asEntity === 'third') {
      deliveryForm.scrollIntoView();
      dispatch(setGlobalLoading(false));
      return;
    }

    if (!isTermsAgreementChecked) {
      termsCheckboxRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setIsCheckboxRequirementVisible(true);
      dispatch(setGlobalLoading(false));
      return;
    }

    // Mapiranje podataka na izlazu iz forme - insuredPerson
    const insuredPerson = insuredPersonForm.getValues();
    const contractor = contractorForm.getValues();


    const insuredPersonData: InsuredPersonInput = {
      firstName: insuredPerson.firstName,
      lastName: insuredPerson.lastName,
      birthDate: insuredPerson.birthDate,
      partnerContactEmail: insuredPerson.partnerContactEmail,
      street: insuredPerson.street,
      streetKompareId: insuredPerson.streetKompareId,
      houseNumber: insuredPerson.houseNumber,
      city: insuredPerson.city,
      zipCode: insuredPerson.zipCode,
      cityKompareId: insuredPerson.cityKompareId,
      oib: insuredPerson.oib,
      insuranceNumber: insuredPerson.insuranceNumber,
      personType: 'person',
    };

    const contractorData: ContractorPartInput = isContractorSameAsInsuredPerson ? {
      birthDate: insuredPerson.birthDate,
      city: insuredPerson.city,
      cityKompareId: insuredPerson.cityKompareId,
      firstName: insuredPerson.firstName,
      houseNumber: insuredPerson.houseNumber,
      lastName: insuredPerson.lastName,
      oib: insuredPerson.oib,
      partnerContactEmail: insuredPerson.partnerContactEmail,
      personType: 'person',
      street: insuredPerson.street,
      streetKompareId: insuredPerson.streetKompareId,
      zipCode: insuredPerson.zipCode,
      asEntity: insuredPerson.asEntity,
    } : {
      birthDate: contractor.birthDate,
      city: contractor.city,
      cityKompareId: contractor.cityKompareId,
      companyName: contractor.companyName,
      firstName: contractor.firstName,
      houseNumber: contractor.houseNumber,
      lastName: contractor.lastName,
      oib: contractor.oib,
      partnerContactEmail: contractor.partnerContactEmail,
      personType: contractor.personType,
      street: contractor.street,
      streetKompareId: contractor.streetKompareId,
      zipCode: contractor.zipCode,
      asEntity: 'contractor',
    };

    const deliveryData: DeliveryInput = {
      city: delivery.city,
      cityKompareId: delivery.cityKompareId,
      houseNumber: delivery.houseNumber,
      name: delivery.name,
      personType: (delivery.personType && delivery.personType?.length > 0) ? delivery.personType : 'person',
      street: delivery.street,
      streetKompareId: delivery.streetKompareId,
      zipCode: delivery.zipCode,
      asEntity: delivery.asEntity,
    };

    const data: ContractorInput = {
      insuredPerson: insuredPersonData,
      contractor: contractorData,
      delivery: deliveryData
    };

    if (soId) {
      setContractor({ variables: { id: soId, offerId, input: data } }).then(() => {
        acceptTerms({ variables: { id: offerId, input: { allianzGDPRAcceptedAt: isAllianzChecked } } }).then(() => {
          navigate(`${AppRoutesAdditional.CHECKOUT.fullPath}/${offerId}`);
          dispatch(showMessage({ message: i18n.t('message.acceptedTermsAndConditions'), type: 'success', duration: 6500 }));
        }).catch(() => {
          dispatch(showMessage({ message: i18n.t('common.globalError'), type: 'error', duration: 6500 }));
        }).finally(() => {
          dispatch(setGlobalLoading(false));
        });
      }).catch(() => {
        dispatch(setGlobalLoading(false));
        dispatch(showMessage({ message: i18n.t('common.globalError'), type: 'error', duration: 6500 }));
      }).finally(() => {
        dispatch(setGlobalLoading(false));
      });
    } else {
      navigateToNoOffer();
      dispatch(setGlobalLoading(false));
    }
  }

  const handleAddressChangeInsured = (address: any) => {
    setAddressData({ ...addressData, insuredPersonAddress: { ...addressData.insuredPersonAddress, ...address }, validation: true });
  };

  const handleAddressChangeContractor = (address: any) => {
    setAddressData({ ...addressData, contractorAddress: { ...addressData.contractorAddress, ...address }, validation: true });
  };

  const handleAddressModalSubmit = (data: any, type: 'insuredPerson' | 'contractor' | 'delivery') => {
    if (type === 'insuredPerson') {
      insuredPersonForm.setFieldValue('street', data.street);
      insuredPersonForm.setFieldValue('zipCode', data.zipCode);
      insuredPersonForm.setFieldValue('city', data.city);
      insuredPersonForm.setFieldValue('houseNumber', data.houseNumber);
      deliveryForm.setFieldValue('street', data.street);
      deliveryForm.setFieldValue('zipCode', data.zipCode);
      deliveryForm.setFieldValue('city', data.city);
      deliveryForm.setFieldValue('houseNumber', data.houseNumber);
    } else if (type === 'contractor') {
      // Ažuriraj contractor form
      contractorForm.setFieldValue('street', data.street);
      contractorForm.setFieldValue('zipCode', data.zipCode);
      contractorForm.setFieldValue('city', data.city);
      contractorForm.setFieldValue('houseNumber', data.houseNumber);
    } else if (type === 'delivery') {
      // Ažuriraj delivery form
      deliveryForm.setFieldValue('street', data.street);
      deliveryForm.setFieldValue('zipCode', data.zipCode);
      deliveryForm.setFieldValue('city', data.city);
      deliveryForm.setFieldValue('houseNumber', data.houseNumber);
    }

    dispatch(closeAddressModal());
  }

  const handleTermsModalOpen = () => {
    dispatch(openTermsAndConditionsAdditionalModal({ offerId }));
  }

  if (loadingOfferAdditional) return <Loader page />;

  if (!formData) return null;

  if (!getOfferAdditionalData?.getOffer) return null;

  return (
    <>
      <Flex vertical gap={24}>
        <Flex vertical gap={8}>
          <Text type="large" bold>
            {i18n.t('pages.offerAdditionalPage.title')}
          </Text>
        </Flex>
        <CardStyle
          radius={12}
          title={<Text type="base" bold>{i18n.t('pages.offerPage.insuredPerson')}</Text>}>
          <Flex vertical gap={24} style={{ marginTop: 24 }}>

            <Checkbox checked={isContractorSameAsInsuredPerson} onChange={() => storeIsSame(!isContractorSameAsInsuredPerson)} label={i18n.t('components.forms.insuredPersonForm.sameAsInsuredPerson')} />

            <InsuredPersonForm
              insuredPerson={formData.insuredPerson}
              ref={insuredPersonForm.formRef}
              onAddressChange={handleAddressChangeInsured}
              storedValues={storedFormValues?.insuredPerson}
              formItems={allInsuredPersonFormItems}
            />
          </Flex>
        </CardStyle>
        {!isContractorSameAsInsuredPerson &&
          <CardStyle
            radius={12}
            title={
              <TextStyle type="base" bold>{i18n.t('pages.offerPage.contractor')}</TextStyle>
            }>
            <ContractorForm contractor={formData.contractor} ref={contractorForm.formRef} onAddressChange={handleAddressChangeContractor} storedValues={{ ...storedFormValues?.contractor }} />
          </CardStyle>}
        <CardStyle
          radius={12}
          title={
            <TextStyle2 type="base" bold>{i18n.t('pages.offerPage.delivery')}</TextStyle2>
          }>
          <DeliveryForm hideContractorOption={isContractorSameAsInsuredPerson} delivery={formData.delivery} addressData={addressData} ref={deliveryForm.formRef} storedValues={storedFormValues?.delivery} />
        </CardStyle>
        <TermsAndConditionsCard>
          <Flex vertical gap={8}>
            <TermsAndConditionsButton onClick={handleTermsModalOpen}>
              <Text type='small' bold color={colors.primary}>{i18n.t("pages.offerPage.terms")}</Text>
              <OpenLinkStyle src={openLink} alt="open-link" />
            </TermsAndConditionsButton>
            <div ref={termsCheckboxRef}>
              <Checkbox checked={isTermsAgreementChecked} onChange={(checked: boolean) => setIsTermsAgreementChecked(checked)} checkboxSize='small' label={<Text type='tiny'>{i18n.t('pages.offerPage.termsDescription')}</Text>} />
            </div>
            {
              getOfferAdditionalData?.getOffer?.product?.brand?.code === 'allianz' && (
                <Checkbox
                  checked={isAllianzChecked}
                  checkboxSize='small'
                  onChange={(checked: boolean) => setIsAllianzChecked(checked)}
                  label={<Text type='tiny'>{i18n.t('pages.offerPage.allianzNotifications')}</Text>}
                />
              )}
          </Flex>
        </TermsAndConditionsCard>

        <CheckboxRequirement visible={isCheckboxRequirementVisible} text={i18n.t('pages.offerPage.checkboxRequirement')} />
      </Flex>
      <SuperFooterBar>
        <BackLink
          text={i18n.t('pages.offerPage.backButton')}
          path={backPath}
        />
        <Button btnType="primary" text={i18n.t('pages.offerPage.submit')}
          onClick={() => { onSubmitClick() }} />
      </SuperFooterBar>
      <AddressModal onAddressModalSubmit={handleAddressModalSubmit} />
    </>
  );
}

export default OfferAdditinalPage;
